import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, ShieldCheck, UserCheck, MessageSquareText, Lock, Sparkles } from "lucide-react";

const STEPS = [
    { n: "1", icon: MessageSquareText, title: "Invia la domanda", text: "Descrivi il tuo dubbio condominiale e allega eventuali documenti (verbali, foto, PDF)." },
    { n: "2", icon: UserCheck, title: "Un professionista la esamina", text: "La richiesta viene presa in carico dal team di NowGestioni coordinato dal Dr. Luigi Colaianni." },
    { n: "3", icon: ShieldCheck, title: "Ricevi la risposta", text: "La risposta arriva nella tua area riservata. Solo tu e il professionista potete vederla." },
    { n: "4", icon: Lock, title: "Privacy garantita", text: "Le tue domande restano riservate. Solo tu e il professionista che le prende in carico possono leggerle." },
];

const FAQS = [
    { q: "Il servizio è davvero gratuito?", a: "Sì. CondominioNow è un servizio informativo gratuito offerto da Nowgestioni S.r.l. Non ci sono costi né abbonamenti nascosti." },
    { q: "Le risposte sono generate da AI?", a: "No. Nessuna intelligenza artificiale genera le risposte. Ogni quesito viene esaminato e risposto da un professionista reale con esperienza sul campo." },
    { q: "Chi può leggere la mia domanda?", a: "Solo tu e il professionista che se ne occupa." },
    { q: "In quanto tempo ricevo la risposta?", a: "Facciamo del nostro meglio per rispondere entro 48 ore lavorative." },
    { q: "Posso allegare documenti?", a: "Sì. Puoi allegare fino a 5 file (JPG, PNG o PDF, max 10MB ciascuno). Aiuta il professionista a inquadrare meglio la situazione." },
];

export default function HowItWorks() {
    return (
        <div className="bg-white">
            <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">COME FUNZIONA IL SERVIZIO FAI UNA DOMANDA</div>
                <h1 className="mt-2 font-serif text-4xl lg:text-6xl font-semibold leading-tight" data-testid="how-title">CondominioNow</h1>
                <p className="mt-4 text-lg text-slate-600 max-w-2xl">Semplice, veloce e pronti ad aiutarti a risolvere i tuoi dubbi condominiali.</p>
            </section>

            <section className="bg-slate-50/60 border-y border-slate-200">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map(s => {
                            const Icon = s.icon;
                            return (
                                <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm">
                                    <div className="size-12 rounded-full bg-primary text-primary-foreground font-serif text-xl font-semibold flex items-center justify-center">{s.n}</div>
                                    <Icon className="size-5 text-primary/60 mt-6" />
                                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{s.title}</h3>
                                    <p className="mt-2 text-slate-600 leading-relaxed">{s.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Domande frequenti</div>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold">Le risposte alle domande più comuni</h2>
                <Accordion type="single" collapsible className="mt-8" data-testid="how-faq">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`}>
                            <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
                <div className="rounded-2xl bg-primary/5 border border-primary/20 p-8 lg:p-10 grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
                    <div className="size-14 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><Lock className="size-6" /></div>
                    <div>
                        <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-widest"><Sparkles className="size-3.5" /> Zero AI, massima privacy</div>
                        <h3 className="mt-2 font-serif text-2xl font-semibold">Persone reali, risposte reali.</h3>
                        <p className="mt-1 text-slate-600">Nessun algoritmo genera i contenuti. Ogni parola è scritta da un professionista.</p>
                    </div>
                    <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full">
                        <Link to="/registrati">Fai una domanda <ArrowRight className="size-4 ml-1" /></Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}
